import type { ColumnDef } from '@tanstack/react-table';
import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';
import type { TPlacementHostCompany } from '@src/shared/types/placement-host-company';
import { generateCustomFieldColumns } from '@src/shared/utils/helpers/generate-custom-field-columns';

export const hostCompaniesColumns = (
  data?: TPlacementHostCompany[]
): ColumnDef<TPlacementHostCompany>[] => {
  return [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Host Company',
      meta: {
        isCopy: true,
      },
      cell: ({ row }) => (
        <div className='flex items-center gap-1'>
          <Avatar className='w-5 h-5 rounded-2xl'>
            <AvatarImage
              src={row.original.logo}
              alt={row.original.name}
            />
            <AvatarFallback>{row.original.name[0]}</AvatarFallback>
          </Avatar>
          <span className='text-sm font-medium text-gray-900'>
            {row.original.name ?? '-'}
          </span>
        </div>
      ),
    },
    {
      id: 'website',
      accessorKey: 'website',
      header: 'Website',
      meta: {
        isCopy: true,
      },
      cell: ({ row }) => (
        <span className='text-primary-1000 text-sm '>
          {row.original.website ?? '-'}
        </span>
      ),
    },
    {
      id: 'contact_name',
      accessorKey: 'contact_name',
      header: 'Contact Name',
      cell: ({ row }) => <span>{row.original.contact_name ?? '-'}</span>,
    },
    {
      id: 'contact_job_title',
      accessorKey: 'contact_job_title',
      header: 'Job Title',
      cell: ({ row }) => <span>{row.original.contact_job_title ?? '-'}</span>,
    },
    {
      id: 'contact_email',
      accessorKey: 'contact_email',
      header: 'Email',
      meta: {
        isCopy: true,
      },
      cell: ({ row }) => <span>{row.original.contact_email ?? '-'}</span>,
    },
    {
      id: 'contact_phone_number',
      accessorKey: 'contact_phone_number',
      header: 'Phone Number',
      meta: {
        isCopy: true,
      },
      cell: ({ row }) => (
        <span>{row.original.contact_phone_number ?? '-'}</span>
      ),
    },
    {
      id: 'address_line_1',
      accessorKey: 'address_line_1',
      header: 'Address Line 1',
      cell: ({ row }) => <span>{row.original.address_line_1 ?? '-'}</span>,
    },
    {
      id: 'address_line_2',
      accessorKey: 'address_line_2',
      header: 'Address Line 2',
      cell: ({ row }) => <span>{row.original.address_line_2 ?? '-'}</span>,
    },
    {
      id: 'city',
      accessorKey: 'city',
      header: 'City',
      cell: ({ row }) => <span>{row.original.city ?? '-'}</span>,
    },
    {
      id: 'state',
      accessorKey: 'state',
      header: 'State',
      cell: ({ row }) => <span>{row.original.state ?? '-'}</span>,
    },
    {
      id: 'country',
      accessorKey: 'country',
      header: 'Country',
      cell: ({ row }) => <span>{row.original.country?.name ?? '-'}</span>,
    },
    {
      id: 'postal_code',
      accessorKey: 'postal_code',
      header: 'Postal Code',
      cell: ({ row }) => <span>{row.original.postal_code ?? '-'}</span>,
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => <span>{row.original.description ?? '-'}</span>,
    },
    ...generateCustomFieldColumns(data || []),
  ];
};
